import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const schema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(60),
  lastName: z.string().trim().min(1, "Last name is required").max(60),
  email: z.string().trim().email("Enter a valid email").max(255),
  phone: z
    .string()
    .trim()
    .min(7, "Enter a valid phone")
    .max(30)
    .regex(/^[0-9+\-()\s.]+$/, "Phone can only contain digits and + - ( ) ."),
  interest: z.enum([
    "Buying",
    "Selling",
    "Request a Showing",
    "Relocation / Referral",
    "Home Valuation",
    "A Private Consultation",
  ]),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
  smsConsent: z.boolean(),
});

export type InquiryValues = z.infer<typeof schema>;

interface InquiryFormProps {
  /** Optional override for the interest options (e.g. modal default). */
  defaultInterest?: InquiryValues["interest"];
  /** Compact spacing for use inside a dialog. */
  compact?: boolean;
  /** Called after the mailto handoff completes. */
  onSubmitted?: () => void;
  /** Show the message field. Default true. */
  showMessage?: boolean;
}

const InquiryForm = ({
  defaultInterest = "Buying",
  compact = false,
  onSubmitted,
  showMessage = true,
}: InquiryFormProps) => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof InquiryValues, string>>>({});
  const [form, setForm] = useState<InquiryValues>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    interest: defaultInterest,
    message: "",
    smsConsent: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    const value =
      target.type === "checkbox" ? target.checked : target.value;
    setForm({ ...form, [target.name]: value });
    if (errors[target.name as keyof InquiryValues]) {
      setErrors({ ...errors, [target.name]: undefined });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof InquiryValues, string>> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0] as keyof InquiryValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      toast({
        title: "Please review the form",
        description: "A few fields need your attention.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    const v = result.data;
    const subject = encodeURIComponent(
      `New inquiry — ${v.firstName} ${v.lastName} (${v.interest})`,
    );
    const body = encodeURIComponent(
      `Name: ${v.firstName} ${v.lastName}\nEmail: ${v.email}\nPhone: ${v.phone}\nInterest: ${v.interest}\n\n${v.message ?? ""}`,
    );
    window.location.href = `mailto:jmsellsnj@gmail.com?subject=${subject}&body=${body}`;

    setTimeout(() => {
      setSubmitting(false);
      toast({
        title: "Thank you",
        description: "John will be in touch within one business day.",
      });
      setForm({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        interest: defaultInterest,
        message: "",
        smsConsent: false,
      });
      onSubmitted?.();
    }, 600);
  };

  const inputCls =
    "w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 text-foreground transition-colors";
  const labelCls =
    "text-[13px] md:text-sm font-semibold tracking-[0.22em] uppercase text-foreground mb-2 block";
  const errorCls = "text-xs text-destructive mt-1";


  return (
    <form
      onSubmit={handleSubmit}
      className={compact ? "space-y-4" : "space-y-6"}
      noValidate
    >
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div>
          <label htmlFor="firstName" className={labelCls}>First Name *</label>
          <input
            id="firstName"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            maxLength={60}
            autoComplete="given-name"
            className={inputCls}
            aria-invalid={!!errors.firstName}
          />
          {errors.firstName && <p className={errorCls}>{errors.firstName}</p>}
        </div>
        <div>
          <label htmlFor="lastName" className={labelCls}>Last Name *</label>
          <input
            id="lastName"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            maxLength={60}
            autoComplete="family-name"
            className={inputCls}
            aria-invalid={!!errors.lastName}
          />
          {errors.lastName && <p className={errorCls}>{errors.lastName}</p>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div>
          <label htmlFor="email" className={labelCls}>Email *</label>
          <input
            id="email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            maxLength={255}
            autoComplete="email"
            className={inputCls}
            aria-invalid={!!errors.email}
          />
          {errors.email && <p className={errorCls}>{errors.email}</p>}
        </div>
        <div>
          <label htmlFor="phone" className={labelCls}>Phone *</label>
          <input
            id="phone"
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            maxLength={30}
            autoComplete="tel"
            className={inputCls}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && <p className={errorCls}>{errors.phone}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="interest" className={labelCls}>I'm Interested In</label>
        <select
          id="interest"
          name="interest"
          value={form.interest}
          onChange={handleChange}
          className={inputCls}
        >
          <option>Buying</option>
          <option>Selling</option>
          <option>Request a Showing</option>
          <option>Relocation / Referral</option>
          <option>Home Valuation</option>
          <option>A Private Consultation</option>
        </select>
      </div>

      {showMessage && (
        <div>
          <label htmlFor="message" className={labelCls}>
            Tell John About Your Goals
          </label>
          <textarea
            id="message"
            name="message"
            rows={compact ? 3 : 4}
            maxLength={1000}
            value={form.message}
            onChange={handleChange}
            className={`${inputCls} resize-none`}
          />
          {errors.message && <p className={errorCls}>{errors.message}</p>}
        </div>
      )}

      <div className="pt-2">
        <label htmlFor="smsConsent" className="flex items-start gap-3 cursor-pointer group">
          <input
            id="smsConsent"
            name="smsConsent"
            type="checkbox"
            checked={form.smsConsent}
            onChange={handleChange}
            className="mt-1 h-4 w-4 shrink-0 accent-primary border border-border rounded-sm cursor-pointer"
          />
          <span className="text-xs leading-relaxed text-muted-foreground font-light">
            By checking this box, I consent to receive calls, emails, and SMS text messages from
            John Mastroianni | Align Right Realty - Homekey about my real estate inquiry — including appointment and showing confirmations, listing and new-property alerts, transaction and document updates, home valuation follow-ups, and occasional market updates. Message frequency varies. Message
            and data rates may apply. Reply <span className="text-foreground">STOP</span> to opt out
            or <span className="text-foreground">HELP</span> for help. Consent is not a condition of
            purchase. See our{" "}
            <Link to="/privacy-policy" className="text-primary hover:underline">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link to="/terms-and-conditions" className="text-primary hover:underline">
              Terms &amp; Conditions
            </Link>
            .
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="group inline-flex items-center gap-3 bg-primary text-primary-foreground px-10 md:px-12 py-5 text-sm font-bold tracking-[0.3em] uppercase shadow-[0_10px_30px_-8px_hsl(var(--primary)/0.55)] hover:shadow-[0_16px_40px_-8px_hsl(var(--primary)/0.75)] hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0"
      >
        {submitting ? "Sending…" : "Send Inquiry"} <Send className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>

    </form>
  );
};

export default InquiryForm;
